import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Building2, 
  MapPin, 
  Calculator, 
  FileText,
  TrendingUp,
  Lightbulb,
  CreditCard,
  BarChart3,
  Home,
  Euro,
  Percent,
  Calendar,
  Users,
  Wrench,
  PiggyBank,
  Scale,
  Clock,
  AlertCircle,
  CheckCircle2,
  Info,
  Zap,
  Camera,
  X,
  Upload,
  Sofa,
  ChefHat,
  Droplets,
  Plug,
  Paintbrush,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import PropertyAutoFill from '@/components/PropertyAutoFill';
import ProjectContextStep from '@/components/ProjectContextStep';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step definitions
const STEPS = [
  { id: 1, title: 'Le Bien', icon: Building2, description: 'Informations sur le bien immobilier' },
  { id: 2, title: 'Marché', icon: BarChart3, description: 'Analyse du marché local' },
  { id: 3, title: 'Financement', icon: PiggyBank, description: 'Votre prêt immobilier' },
  { id: 4, title: 'Travaux', icon: Wrench, description: 'Travaux et équipement' },
  { id: 5, title: 'Location', icon: Home, description: 'Scénarios de location' },
  { id: 6, title: 'Charges & Fiscalité', icon: Scale, description: 'Charges et impact fiscal' },
  { id: 7, title: 'Revente', icon: TrendingUp, description: 'Stratégie de sortie' },
  { id: 8, title: 'Photos', icon: Camera, description: 'Photos du bien' },
  { id: 9, title: 'Résultats', icon: Lightbulb, description: 'Recommandation finale' },
  { id: 10, title: 'Contexte', icon: Users, description: 'Contexte qualitatif du projet' },
  { id: 11, title: 'Dossier PDF', icon: FileText, description: 'Génération du dossier' }
];

export default function SimulationWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [marketDataLoading, setMarketDataLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [simulationId, setSimulationId] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [showAutoFill, setShowAutoFill] = useState(false);
  const [devisAnalyzing, setDevisAnalyzing] = useState(false);
  const [analyzedDevis, setAnalyzedDevis] = useState([]); // Array of analyzed devis
  const [premiumDocumentUsed, setPremiumDocumentUsed] = useState(false);
  const [premiumDocumentType, setPremiumDocumentType] = useState(null);
  const [premiumDocumentUrl, setPremiumDocumentUrl] = useState(null);
  const [contextSaving, setContextSaving] = useState(false);
  const [contextSummary, setContextSummary] = useState(null);
  
  // Handle auto-fill data from PropertyAutoFill component
  const handleAutoFillData = (extractedData) => {
    console.log('[SimulationWizard] Received auto-fill data:', extractedData);
    
    // Check for premium document
    if (extractedData._premiumDocumentUsed) {
      setPremiumDocumentUsed(true);
      setPremiumDocumentType(extractedData._premiumDocumentType);
      setPremiumDocumentUrl(extractedData._premiumDocumentUrl);
      console.log('[SimulationWizard] Premium document detected:', extractedData._premiumDocumentType);
    }
    
    // Map extracted fields to form data
    setFormData(prev => {
      const newData = {
        ...prev,
        // Property basics
        propertyPrice: extractedData.purchasePrice || prev.propertyPrice,
        surface: extractedData.surface || prev.surface,
        nbRooms: extractedData.rooms?.toString() || prev.nbRooms,
        nbBedrooms: extractedData.bedrooms?.toString() || prev.nbBedrooms,
        // Address
        city: extractedData.city || prev.city,
        postalCode: extractedData.postalCode?.toString() || prev.postalCode,
        streetAddress: extractedData.address || prev.streetAddress,
        // DPE
        dpe: extractedData.dpeRating || prev.dpe,
        // Year built
        yearBuilt: extractedData.yearBuilt || prev.yearBuilt,
        // Charges
        monthlyCharges: extractedData.monthlyCharges || prev.monthlyCharges,
        // Estimated rent
        estimatedRent: extractedData.estimatedRent || prev.estimatedRent,
        // Work estimate
        workEstimate: extractedData.workEstimate || prev.workEstimate,
        // Contacts
        agentName: extractedData.agencyName || prev.agentName,
        notaryName: extractedData.notaryName || prev.notaryName,
        // Dates - map to existing form fields
        datePromesseVente: extractedData.signatureDate || prev.datePromesseVente,
        dateAchatTheorique: extractedData.finalSaleDeadline || prev.dateAchatTheorique,
        // Store extraction metadata
        _extractionSource: extractedData._extraction?.source_type || 'auto',
        _extractionConfidence: extractedData._extraction?.confidence || 0
      };
      console.log('[SimulationWizard] Updated form data:', newData);
      return newData;
    });
  };
  
  // Form data state - all steps
  const [formData, setFormData] = useState({
    // Step 1: Property Info
    propertyPrice: '',
    surface: '',
    nbRooms: '2',
    nbBedrooms: '1',  // Nombre de chambres pour Airbnb
    nbGuests: '4',    // Capacité d'accueil (couchages)
    
    // Address
    streetAddress: '',
    city: '',
    postalCode: '',
    
    // DPE & Diagnostics
    dpe: 'D',  // A à G
    electricityDiag: 'conforme', // conforme, anomalies, non_conforme
    sanitationDiag: 'conforme', // conforme, anomalies, non_conforme
    
    // Contacts
    agentName: '',
    agentPhone: '',
    agentEmail: '',
    notaryName: '',
    notaryOffice: '',
    notaryPhone: '',
    
    // Dates clés du projet
    dateOffreAchat: '',      // Date signature offre d'achat
    datePromesseVente: '',   // Date signature promesse de vente
    dateAchatTheorique: '',  // Date théorique achat (acte authentique)
    
    // Photos (base64 or URLs)
    photos: [], // [{id, label, data}]
    
    // Work Documents (photos + PDFs for renovation justification)
    workDocuments: [], // [{doc_id, type, label, description, url, file}]
    
    // Step 2: Market Data (auto-filled)
    airbnbPricePerNight: null,
    airbnbOccupancyRate: 70,
    traditionalRentPerM2: null,
    marketDataSource: '',
    airbnbListings: [], // Annonces Airbnb scrapées avec URLs
    
    // Step 3: Financing
    loanType: '100', // 100% or 110%
    loanDuration: '20',
    interestRate: '3.50',  // Default interest rate
    insuranceRate: '0.34',
    notaryFees: '',
    
    // Step 4: Rental Scenarios
    rentalType: 'both', // 'traditional', 'airbnb', 'both'
    airbnbDaysPerYear: '120',
    traditionalMonthlyRent: '',  // Will be auto-filled by market data
    airbnbNightlyRate: '',       // Will be auto-filled by market data
    
    // Step 5: Charges & Taxes + Travaux détaillés
    propertyTax: '1500',  // Default property tax
    insurancePNO: '300',
    managementFees: '0',
    maintenanceProvision: '500',
    airbnbCleaningFee: '50',
    airbnbPlatformFee: '3',
    rfr: '',
    taxParts: '1',
    taxRegime: 'micro-bic', // 'micro-bic' or 'reel'
    
    // Travaux détaillés (checkboxes + montants)
    worksElectricity: false,
    worksElectricityAmount: '0',
    worksPlumbing: false,
    worksPlumbingAmount: '0',
    worksRoof: false,
    worksRoofAmount: '0',
    worksInterior: false,
    worksInteriorAmount: '0',
    worksInsulation: false,
    worksInsulationAmount: '0',
    worksOther: false,
    worksOtherAmount: '0',
    
    // Équipement Airbnb (slider occasion/neuf)
    airbnbEquipmentLevel: 'occasion', // occasion, neuf
    equipKitchenFurniture: false,
    equipKitchenFurnitureAmount: '0',
    equipBathroomRenovation: false,
    equipBathroomRenovationAmount: '0',
    equipWaterHeater: false,
    equipWaterHeaterAmount: '0',
    equipAppliances: false,
    equipAppliancesAmount: '0',
    equipFurniture: false,
    equipFurnitureAmount: '0',
    equipLinenKit: false,
    equipLinenKitAmount: '0',
    
    // Step 6: Exit Strategy
    holdingDuration: '10',
    expectedAppreciation: '2',
    isMainResidence: false,
    
    // Step 10 (NEW): Project Context — qualitative arguments for the banker
    contextFamilyStatus: '',
    contextFamilyProjects: '',
    contextProximity: '',
    contextPersonalUse: '',
    contextNearbyAttractions: [],
    contextLocalDevelopment: '',
    contextInvestmentStrategy: [],
    contextFreeText: '',
    
    // Results (calculated)
    results: null
  });

  // Update form data helper
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Equipment price estimates (occasion vs neuf)
  const EQUIPMENT_PRICES = {
    occasion: {
      equipKitchenFurniture: 800,      // Meubles cuisine LBC
      equipBathroomRenovation: 1500,   // Réfection SDB
      equipWaterHeater: 200,           // Chauffe-eau occasion
      equipAppliances: 600,            // Électroménager LBC
      equipFurniture: 1200,            // Mobilier LBC
      equipLinenKit: 150               // Trousseau cuisine/linge
    },
    neuf: {
      equipKitchenFurniture: 2500,     // Meubles cuisine Electrodepot/But
      equipBathroomRenovation: 4000,   // Réfection SDB neuf
      equipWaterHeater: 500,           // Chauffe-eau neuf
      equipAppliances: 1500,           // Électroménager neuf
      equipFurniture: 3000,            // Mobilier neuf
      equipLinenKit: 400               // Trousseau neuf
    }
  };

  // Works price estimates based on surface
  const getWorksEstimate = (type, surface) => {
    const sqm = parseFloat(surface) || 35;
    const estimates = {
      worksElectricity: Math.round(sqm * 80),      // 80€/m²
      worksPlumbing: Math.round(sqm * 60),         // 60€/m²
      worksRoof: 3000,                              // Forfait
      worksInterior: Math.round(sqm * 150),        // 150€/m²
      worksInsulation: Math.round(sqm * 50),       // 50€/m²
      worksOther: 1000                              // Forfait
    };
    return estimates[type] || 0;
  };

  // Update equipment amounts when level changes
  // Uses functional setState so deps can stay limited to the level only
  // (we want to refresh amounts ONLY when the level toggles, not on every checkbox change)
  useEffect(() => {
    setFormData(prev => {
      const level = prev.airbnbEquipmentLevel;
      const prices = EQUIPMENT_PRICES[level];
      const updates = {};
      Object.keys(prices).forEach(key => {
        if (prev[key]) { // Only update if checkbox is checked
          updates[`${key}Amount`] = prices[key].toString();
        }
      });
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
    // EQUIPMENT_PRICES is a module-level constant; safe to omit from deps
  }, [formData.airbnbEquipmentLevel]);

  // Photo presets for upload
  const PHOTO_PRESETS = [
    { id: 'facade', label: 'Façade / Extérieur' },
    { id: 'salon', label: 'Salon / Séjour' },
    { id: 'chambre1', label: 'Chambre principale' },
    { id: 'chambre2', label: 'Chambre 2' },
    { id: 'cuisine', label: 'Cuisine' },
    { id: 'sdb', label: 'Salle de bains' },
    { id: 'wc', label: 'WC' },
    { id: 'balcon', label: 'Balcon / Terrasse' },
    { id: 'parking', label: 'Parking / Garage' },
    { id: 'autre', label: 'Autre' }
  ];

  // Handle photo upload
  const handlePhotoUpload = async (photoId, file) => {
    if (!file) return;
    
    setPhotoUploading(true);
    try {
      // First show preview in local state using base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        const preset = PHOTO_PRESETS.find(p => p.id === photoId);
        
        // Update local state immediately for preview
        setFormData(prev => {
          const existingPhotos = prev.photos.filter(p => p.id !== photoId);
          return {
            ...prev,
            photos: [...existingPhotos, { id: photoId, label: preset?.label || photoId, data: base64 }]
          };
        });
        
        // If we have a simulation ID, upload to server
        if (simulationId) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            formDataUpload.append('photo_id', photoId);
            formDataUpload.append('label', preset?.label || photoId);
            
            await axiosInstance.post(
              `${API}/photos/upload/${simulationId}`,
              formDataUpload,
              { 
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
              }
            );
            toast.success(`Photo "${preset?.label}" enregistrée`);
          } catch (uploadError) {
            console.error('Upload error:', uploadError);
            // Photo still shows locally, just not persisted
            toast.info(`Photo ajoutée (sera sauvegardée avec la simulation)`);
          }
        } else {
          toast.success(`Photo "${preset?.label}" ajoutée`);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors du chargement de la photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Remove photo
  const removePhoto = async (photoId) => {
    // If we have a simulation ID, delete from server
    if (simulationId) {
      try {
        await axiosInstance.delete(`${API}/photos/${simulationId}/${photoId}`, { withCredentials: true });
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
    toast.success('Photo supprimée');
  };

  // Calculate total works cost
  const calculateTotalWorks = () => {
    let total = 0;
    if (formData.worksElectricity) total += parseFloat(formData.worksElectricityAmount) || 0;
    if (formData.worksPlumbing) total += parseFloat(formData.worksPlumbingAmount) || 0;
    if (formData.worksRoof) total += parseFloat(formData.worksRoofAmount) || 0;
    if (formData.worksInterior) total += parseFloat(formData.worksInteriorAmount) || 0;
    if (formData.worksInsulation) total += parseFloat(formData.worksInsulationAmount) || 0;
    if (formData.worksOther) total += parseFloat(formData.worksOtherAmount) || 0;
    return total;
  };

  // Calculate total equipment cost
  const calculateTotalEquipment = () => {
    let total = 0;
    if (formData.equipKitchenFurniture) total += parseFloat(formData.equipKitchenFurnitureAmount) || 0;
    if (formData.equipBathroomRenovation) total += parseFloat(formData.equipBathroomRenovationAmount) || 0;
    if (formData.equipWaterHeater) total += parseFloat(formData.equipWaterHeaterAmount) || 0;
    if (formData.equipAppliances) total += parseFloat(formData.equipAppliancesAmount) || 0;
    if (formData.equipFurniture) total += parseFloat(formData.equipFurnitureAmount) || 0;
    if (formData.equipLinenKit) total += parseFloat(formData.equipLinenKitAmount) || 0;
    return total;
  };

  // Load current user info
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await axiosInstance.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Error loading user:', error);
        navigate('/');
      }
    };
    loadUser();
  }, [navigate]);

  // Calculate notary fees automatically
  useEffect(() => {
    if (!formData.propertyPrice) return;
    const price = parseFloat(formData.propertyPrice) || 0;
    const notary = Math.round(price * 0.08); // ~8% for old property
    setFormData(prev => ({ ...prev, notaryFees: notary.toString() }));
  }, [formData.propertyPrice]);

  // Auto-save simulation when results are calculated
  const saveSimulation = async (results) => {
    // Build a clean payload without base64 photos (they're uploaded separately)
    const { photos, ...formDataWithoutPhotos } = formData;
    const simulationData = {
      ...formDataWithoutPhotos,
      results,
      is_vip: currentUser?.is_vip || false
    };
    
    // Remove file objects from workDocuments before sending
    simulationData.workDocuments = formData.workDocuments.map(doc => ({
      id: doc.id,
      type: doc.type,
      label: doc.label,
      description: doc.description
    }));
    
    // Remove any other large/unnecessary fields
    delete simulationData._extractionSource;
    delete simulationData._extractionConfidence;
    
    // Include analyzed devis data for PDF generation
    simulationData.analyzedDevis = analyzedDevis.map(d => ({
      id: d.id,
      filename: d.filename,
      company: d.company,
      devis_info: d.devis_info,
      line_items: d.line_items,
      totals: d.totals,
      work_categories_summary: d.work_categories_summary,
      notes: d.notes
    }));
    
    // Include premium document info
    simulationData.premiumDocumentUsed = premiumDocumentUsed;
    simulationData.premiumDocumentType = premiumDocumentType;
    simulationData.premiumDocumentUrl = premiumDocumentUrl;
    
    const response = await axiosInstance.post(`${API}/simulations/save-wizard`, simulationData, {
      withCredentials: true
    });
    
    if (response.data.simulation_id) {
      setSimulationId(response.data.simulation_id);
      
      // Upload work documents in background (don't block the flow)
      if (formData.workDocuments.length > 0) {
        for (const doc of formData.workDocuments) {
          if (doc.file) {
            const formDataUpload = new FormData();
            formDataUpload.append('file', doc.file);
            formDataUpload.append('doc_type', doc.type);
            formDataUpload.append('label', doc.label || (doc.type === 'photo' ? 'Photo travaux' : 'Devis'));
            formDataUpload.append('description', doc.description || '');
            
            try {
              await axiosInstance.post(
                `${API}/work-documents/${response.data.simulation_id}`,
                formDataUpload,
                { withCredentials: true }
              );
            } catch (uploadError) {
              console.error('Error uploading work document:', uploadError);
            }
          }
        }
      }
      
      return response.data.simulation_id;
    }
    
    throw new Error('No simulation_id in response');
  };

  // Generate PDF for VIP users (free)
  const generatePdfVip = async () => {
    if (!simulationId) {
      toast.error('Veuillez d\'abord calculer les résultats');
      return;
    }
    
    setPdfGenerating(true);
    try {
      const response = await axiosInstance.get(`${API}/simulations/${simulationId}/export-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Download PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simulation-${simulationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Download Word document
  const [wordGenerating, setWordGenerating] = useState(false);
  
  const handleDownloadWord = async () => {
    if (!simulationId) {
      toast.error('Veuillez d\'abord calculer les résultats');
      return;
    }
    
    setWordGenerating(true);
    try {
      const response = await axiosInstance.get(`${API}/simulations/${simulationId}/export-word`, {
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      
      // Download Word
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier-${simulationId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Document Word téléchargé !');
    } catch (error) {
      console.error('Error generating Word:', error);
      toast.error('Erreur lors de la génération du Word');
    } finally {
      setWordGenerating(false);
    }
  };

  // Handle devis travaux PDF upload and AI analysis (multi-devis)
  const handleDevisUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Veuillez uploader un fichier PDF');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    
    setDevisAnalyzing(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axiosInstance.post(`${API}/property/extract-devis`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        const devisEntry = {
          id: `devis_${Date.now()}`,
          filename: file.name,
          ...data
        };
        setAnalyzedDevis(prev => [...prev, devisEntry]);
        toast.success(`Devis "${data.company?.name || file.name}" ajouté !`);
      } else {
        toast.error(response.data.error || 'Erreur d\'analyse du devis');
      }
    } catch (error) {
      console.error('Devis upload error:', error);
      toast.error('Erreur lors de l\'analyse du devis');
    } finally {
      setDevisAnalyzing(false);
      e.target.value = '';
    }
  };
  
  // Remove a devis from the list
  const removeDevis = (devisId) => {
    setAnalyzedDevis(prev => prev.filter(d => d.id !== devisId));
    toast.success('Devis supprimé');
  };
  
  // Apply ALL devis data to work fields (cumulative)
  const applyAllDevisToWorks = () => {
    if (analyzedDevis.length === 0) return;
    
    // Sum all categories across all devis
    const totals = { electricite: 0, plomberie: 0, toiture: 0, interieur: 0, isolation: 0, autre: 0 };
    let grandTotalTTC = 0;
    
    for (const devis of analyzedDevis) {
      const cats = devis.work_categories_summary || {};
      totals.electricite += cats.electricite || 0;
      totals.plomberie += cats.plomberie || 0;
      totals.toiture += cats.toiture || 0;
      totals.interieur += cats.interieur || 0;
      totals.isolation += cats.isolation || 0;
      totals.autre += cats.autre || 0;
      grandTotalTTC += devis.totals?.total_ttc || 0;
    }
    
    if (totals.electricite > 0) { updateFormData('worksElectricity', true); updateFormData('worksElectricityAmount', Math.round(totals.electricite).toString()); }
    if (totals.plomberie > 0) { updateFormData('worksPlumbing', true); updateFormData('worksPlumbingAmount', Math.round(totals.plomberie).toString()); }
    if (totals.toiture > 0) { updateFormData('worksRoof', true); updateFormData('worksRoofAmount', Math.round(totals.toiture).toString()); }
    if (totals.interieur > 0) { updateFormData('worksInterior', true); updateFormData('worksInteriorAmount', Math.round(totals.interieur).toString()); }
    if (totals.isolation > 0) { updateFormData('worksInsulation', true); updateFormData('worksInsulationAmount', Math.round(totals.isolation).toString()); }
    if (totals.autre > 0) { updateFormData('worksOther', true); updateFormData('worksOtherAmount', Math.round(totals.autre).toString()); }
    
    // Update total renovation cost
    if (grandTotalTTC > 0) {
      updateFormData('renovationCost', Math.round(grandTotalTTC).toString());
    }
    
    // Store devis data in formData for PDF generation
    setFormData(prev => ({
      ...prev,
      analyzedDevis: analyzedDevis,
      workDocuments: [
        ...prev.workDocuments.filter(d => !d.id.startsWith('devis_')),
        ...analyzedDevis.map(d => ({
          id: d.id,
          type: 'devis',
          label: `Devis ${d.company?.name || 'travaux'}`,
          description: `Total TTC: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(d.totals?.total_ttc || 0)}`
        }))
      ]
    }));
    
    toast.success(`${analyzedDevis.length} devis appliqués — Total: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(grandTotalTTC)}`);
  };

  // Payment with Stripe
  const handleStripePayment = async () => {
    if (!simulationId) {
      toast.error('Veuillez d\'abord calculer les résultats');
      return;
    }
    
    setPaymentProcessing(true);
    try {
      const response = await axiosInstance.post(`${API}/payments/create-checkout`, {
        simulation_id: simulationId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // VIP users get free access
      if (response.data.is_vip) {
        toast.success('Accès VIP - Téléchargement gratuit !');
        generatePdfVip();
        return;
      }
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation du paiement');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Payment with PayPal
  const handlePayPalPayment = async () => {
    if (!simulationId) {
      toast.error('Veuillez d\'abord calculer les résultats');
      return;
    }
    
    setPaymentProcessing(true);
    try {
      const response = await axiosInstance.post(`${API}/paypal/create-order`, {
        simulation_id: simulationId,
        user_email: currentUser?.email || '',
        success_url: `${window.location.origin}/simulation?step=10&payment=success&paypal=true`,
        cancel_url: `${window.location.origin}/simulation?step=10&payment=cancelled`
      }, { withCredentials: true });
      
      // VIP users get free access
      if (response.data.is_vip) {
        toast.success('Accès VIP - Téléchargement gratuit !');
        generatePdfVip();
        return;
      }
      
      // Redirect to PayPal approval
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Erreur: URL PayPal non disponible');
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      if (error.response?.status === 503) {
        toast.error('PayPal non configuré - Veuillez utiliser Stripe');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation PayPal');
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Handle payment based on selected method
  const handlePayment = () => {
    if (selectedPaymentMethod === 'stripe') {
      handleStripePayment();
    } else {
      handlePayPalPayment();
    }
  };

  // Fetch market data when location changes
  const fetchMarketData = async () => {
    if (!formData.city || !formData.postalCode) return;
    
    setMarketDataLoading(true);
    let airbnbSuccess = false;
    let inseeSuccess = false;
    
    try {
      // Fetch Airbnb data with guests parameter
      try {
        const airbnbResponse = await axiosInstance.post(`${API}/market-data/airbnb-realtime`, {
          location: formData.city,
          checkin: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          checkout: new Date(Date.now() + 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          adults: parseInt(formData.nbGuests) || 2,
          bedrooms: parseInt(formData.nbBedrooms) || 1
        }, { withCredentials: true });
        
        // Handle different response formats
        const avgPrice = airbnbResponse.data.stats?.avg_price_per_night || 
                        airbnbResponse.data.average_price ||
                        airbnbResponse.data.stats?.median_price_per_night;
        const occupancy = airbnbResponse.data.stats?.occupancy || 70;
        
        // Store Airbnb listings with URLs for PDF
        const listings = airbnbResponse.data.listings || [];
        if (listings.length > 0) {
          updateFormData('airbnbListings', listings.slice(0, 10)); // Keep top 10
        }
        
        if (avgPrice && avgPrice > 0) {
          updateFormData('airbnbPricePerNight', avgPrice);
          updateFormData('airbnbNightlyRate', Math.round(avgPrice).toString());
          updateFormData('airbnbOccupancyRate', occupancy);
          airbnbSuccess = true;
        }
      } catch (airbnbError) {
        console.log('Airbnb data not available, using estimates');
      }
      
      // Fetch traditional rental data (INSEE) - this should always work
      try {
        const dept = formData.postalCode.substring(0, 2);
        const surface = parseFloat(formData.surface) || 50;
        const inseeResponse = await axiosInstance.get(`${API}/market-data/insee/${dept}?surface=${surface}`, { withCredentials: true });
        
        if (inseeResponse.data.price_per_m2) {
          updateFormData('traditionalRentPerM2', inseeResponse.data.price_per_m2);
          const monthlyRent = inseeResponse.data.estimated_monthly_rent || 
                              Math.round(inseeResponse.data.price_per_m2 * surface);
          updateFormData('traditionalMonthlyRent', monthlyRent.toString());
          inseeSuccess = true;
        }
      } catch (inseeError) {
        console.log('INSEE data error:', inseeError);
      }
      
      // Try LeBonCoin for real rental data
      try {
        const lbcResponse = await axiosInstance.post(`${API}/market-data/leboncoin-realtime`, {
          location: formData.city,
          postal_code: formData.postalCode,
          surface_min: Math.max(10, parseFloat(formData.surface) - 10),
          surface_max: parseFloat(formData.surface) + 15,
          rooms: parseInt(formData.nbRooms) || 2,
          radius_km: 15
        }, { withCredentials: true });
        
        if (lbcResponse.data.stats?.avg_price && lbcResponse.data.stats.avg_price > 0) {
          updateFormData('traditionalRentPerM2', Math.round(lbcResponse.data.stats.avg_price / parseFloat(formData.surface)));
          updateFormData('traditionalMonthlyRent', Math.round(lbcResponse.data.stats.avg_price).toString());
          updateFormData('lbcListings', lbcResponse.data.listings || []);
          inseeSuccess = true; // Override with real data
          toast.success('Données LeBonCoin temps réel récupérées');
        }
      } catch (lbcError) {
        console.log('LeBonCoin data not available, using INSEE fallback');
      }
      
      // Update source message
      if (airbnbSuccess && inseeSuccess) {
        updateFormData('marketDataSource', `Données Airbnb (${formData.nbGuests} pers.) + INSEE 2025`);
        toast.success('Données de marché récupérées');
      } else if (inseeSuccess) {
        updateFormData('marketDataSource', 'Données INSEE 2025 (Airbnb non disponible)');
        toast.success('Données INSEE récupérées');
      } else if (airbnbSuccess) {
        updateFormData('marketDataSource', 'Données Airbnb temps réel (INSEE non disponible)');
        toast.success('Données Airbnb récupérées');
      } else {
        updateFormData('marketDataSource', 'Estimation (données non disponibles)');
        toast.info('Utilisation des estimations par défaut');
      }
    } catch (error) {
      console.error('Market data error:', error);
      updateFormData('marketDataSource', 'Estimation (données non disponibles)');
      toast.info('Utilisation des estimations par défaut');
    } finally {
      setMarketDataLoading(false);
    }
  };

  // Fetch interest rate
  const fetchInterestRate = async () => {
    try {
      const response = await axiosInstance.get(`${API}/market-rates?duration=${formData.loanDuration}`, { withCredentials: true });
      if (response.data.rate) {
        updateFormData('interestRate', response.data.rate.toString());
      }
    } catch (error) {
      // Default rate
      updateFormData('interestRate', '3.5');
    }
  };

  useEffect(() => {
    fetchInterestRate();
  }, [formData.loanDuration]);

  // Calculate results - returns true on success, false on failure
  const calculateResults = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API}/simulations/calculate-full`, {
        // Property
        property_price: parseFloat(formData.propertyPrice),
        surface_m2: parseFloat(formData.surface),
        nb_rooms: parseInt(formData.nbRooms),
        location: `${formData.city} ${formData.postalCode}`,
        renovation_cost: parseFloat(formData.renovationCost) || 0,
        notary_fees: parseFloat(formData.notaryFees) || 0,
        
        // Financing
        loan_type: formData.loanType,
        loan_duration: parseInt(formData.loanDuration),
        interest_rate: parseFloat(formData.interestRate),
        insurance_rate: parseFloat(formData.insuranceRate),
        
        // Rental
        rental_type: formData.rentalType,
        airbnb_days_per_year: parseInt(formData.airbnbDaysPerYear),
        traditional_monthly_rent: parseFloat(formData.traditionalMonthlyRent) || 0,
        airbnb_nightly_rate: parseFloat(formData.airbnbNightlyRate) || 0,
        
        // Charges
        property_tax: parseFloat(formData.propertyTax) || 0,
        insurance_pno: parseFloat(formData.insurancePNO) || 0,
        management_fees: parseFloat(formData.managementFees) || 0,
        maintenance_provision: parseFloat(formData.maintenanceProvision) || 0,
        airbnb_cleaning_fee: parseFloat(formData.airbnbCleaningFee) || 0,
        airbnb_platform_fee: parseFloat(formData.airbnbPlatformFee) || 0,
        
        // Taxes
        rfr: parseFloat(formData.rfr) || 0,
        tax_parts: parseFloat(formData.taxParts) || 1,
        tax_regime: formData.taxRegime,
        
        // Exit
        holding_duration: parseInt(formData.holdingDuration),
        expected_appreciation: parseFloat(formData.expectedAppreciation),
        is_main_residence: formData.isMainResidence
      }, { withCredentials: true });
      
      updateFormData('results', response.data);
      
      // Save simulation - errors propagate to catch block
      try {
        await saveSimulation(response.data);
        toast.success('Calculs terminés et sauvegardés');
      } catch (saveError) {
        console.error('Save error:', saveError);
        toast.error('Calculs OK mais erreur de sauvegarde. Veuillez réessayer.');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Erreur lors des calculs');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return formData.propertyPrice && formData.surface && formData.city && formData.postalCode;
      case 2:
        return true; // Market data is optional
      case 3:
        return formData.loanDuration && formData.interestRate;
      case 4:
        return true; // Travaux is optional
      case 5:
        // Location: Allow to proceed if rental type is set and at least one rent is filled OR market data provided estimates
        const hasTraditionalRent = formData.traditionalMonthlyRent || formData.estimatedMonthlyRent;
        const hasAirbnbRate = formData.airbnbNightlyRate || formData.estimatedAirbnbDaily;
        return formData.rentalType && (hasTraditionalRent || hasAirbnbRate);
      case 6:
        return formData.propertyTax; // Charges & Fiscalité requires tax
      case 7:
        return formData.holdingDuration; // Revente requires holding duration
      case 8:
        return true; // Photos are optional
      case 9:
        return formData.results; // Results need calculation
      case 10:
        return true; // Project Context is fully optional
      default:
        return true;
    }
  };

  // Get validation error message for current step
  const getValidationMessage = () => {
    switch (currentStep) {
      case 1:
        if (!formData.propertyPrice) return "Veuillez saisir le prix du bien";
        if (!formData.surface) return "Veuillez saisir la surface en m²";
        if (!formData.city) return "Veuillez saisir la ville";
        if (!formData.postalCode) return "Veuillez saisir le code postal";
        return null;
      case 3:
        if (!formData.interestRate) return "Veuillez saisir le taux d'intérêt";
        return null;
      case 5:
        if (!formData.traditionalMonthlyRent && !formData.airbnbNightlyRate && !formData.estimatedMonthlyRent && !formData.estimatedAirbnbDaily) 
          return "Veuillez saisir au moins un loyer (traditionnel ou Airbnb)";
        return null;
      case 6:
        if (!formData.propertyTax) return "Veuillez saisir la taxe foncière";
        return null;
      case 7:
        if (!formData.holdingDuration) return "Veuillez sélectionner la durée de détention";
        return null;
      default:
        return null;
    }
  };

  const goNext = async () => {
    if (currentStep === 2) {
      fetchMarketData();
    }
    if (currentStep === 8) {
      const success = await calculateResults();
      if (!success) return; // Don't advance if calculation/save failed
    }
    if (currentStep === 10) {
      // Persist Project Context + generate AI synthesis before PDF step
      const success = await saveProjectContext();
      if (!success) return;
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Save project context (Step 10) and regenerate AI summary
  const saveProjectContext = async () => {
    if (!simulationId) {
      // Should never happen — step 9 (Résultats) ensures the sim is saved
      toast.error('Simulation introuvable, retournez aux résultats.');
      return false;
    }
    setContextSaving(true);
    try {
      const response = await axiosInstance.post(
        `${API}/simulations/${simulationId}/context`,
        {
          familyStatus: formData.contextFamilyStatus || '',
          familyProjects: formData.contextFamilyProjects || '',
          proximity: formData.contextProximity || '',
          personalUse: formData.contextPersonalUse || '',
          nearbyAttractions: formData.contextNearbyAttractions || [],
          localDevelopment: formData.contextLocalDevelopment || '',
          investmentStrategy: formData.contextInvestmentStrategy || [],
          freeText: formData.contextFreeText || ''
        },
        { withCredentials: true }
      );
      if (response.data?.context_summary) {
        setContextSummary(response.data.context_summary);
        toast.success('Contexte enregistré — synthèse IA générée');
      } else {
        toast.success('Contexte enregistré');
      }
      return true;
    } catch (err) {
      console.error('saveProjectContext error', err);
      toast.error("Impossible d'enregistrer le contexte. Vous pouvez continuer.");
      // Don't block the wizard on a context save failure
      return true;
    } finally {
      setContextSaving(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    if (step <= currentStep || step === currentStep + 1) {
      setCurrentStep(step);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                className="text-slate-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Nouvelle Simulation</h1>
                <p className="text-sm text-slate-500">Étape {currentStep} sur {STEPS.length}</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-blue-600">
                {premiumDocumentUsed ? '5,99€' : '4,99€'}
              </span>
              <p className="text-xs text-slate-500">
                {premiumDocumentUsed ? 'TTC (avec document premium)' : 'TTC'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200 py-4 overflow-x-auto">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between min-w-max gap-2">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              const isClickable = step.id <= currentStep || step.id === currentStep + 1;
              
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isClickable && goToStep(step.id)}
                    disabled={!isClickable}
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                      isCurrent 
                        ? 'bg-blue-50 text-blue-600' 
                        : isCompleted 
                          ? 'text-green-600 hover:bg-green-50' 
                          : isClickable
                            ? 'text-slate-400 hover:bg-slate-50'
                            : 'text-slate-300 cursor-not-allowed'
                    }`}
                    data-testid={`step-${step.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCurrent 
                        ? 'bg-blue-600 text-white' 
                        : isCompleted 
                          ? 'bg-green-500 text-white' 
                          : 'bg-slate-200 text-slate-500'
                    }`}>
                      {isCompleted ? <Check className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                    </div>
                    <span className="text-xs font-medium whitespace-nowrap">{step.title}</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`w-8 h-0.5 ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-3xl mx-auto">
          
          {/* Step 1: Property Info */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-1-content">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Informations sur le bien</h2>
                    <p className="text-sm text-slate-500">Décrivez le bien que vous souhaitez acquérir</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowAutoFill(true)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="auto-fill-button"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Remplissage auto
                </Button>
              </div>

              {/* Auto-fill info banner */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Gagnez du temps avec le remplissage automatique
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Collez le texte d'une annonce ou importez un document (fiche agence, promesse de vente) 
                      et l'IA remplira automatiquement les champs.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="propertyPrice" className="text-slate-700 mb-2 block">
                      Prix d'acquisition <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="propertyPrice"
                        type="number"
                        value={formData.propertyPrice}
                        onChange={(e) => updateFormData('propertyPrice', e.target.value)}
                        placeholder="150000"
                        className="pl-10"
                        data-testid="input-property-price"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="surface" className="text-slate-700 mb-2 block">
                      Surface (m²) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="surface"
                      type="number"
                      value={formData.surface}
                      onChange={(e) => updateFormData('surface', e.target.value)}
                      placeholder="50"
                      data-testid="input-surface"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="city" className="text-slate-700 mb-2 block">
                      Ville <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateFormData('city', e.target.value)}
                        placeholder="Paris"
                        className="pl-10"
                        data-testid="input-city"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="postalCode" className="text-slate-700 mb-2 block">
                      Code postal <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postalCode"
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => updateFormData('postalCode', e.target.value)}
                      placeholder="75001"
                      maxLength={5}
                      data-testid="input-postal-code"
                    />
                  </div>
                </div>

                {/* Full address */}
                <div>
                  <Label htmlFor="streetAddress" className="text-slate-700 mb-2 block">
                    Adresse complète (optionnel)
                  </Label>
                  <Input
                    id="streetAddress"
                    type="text"
                    value={formData.streetAddress}
                    onChange={(e) => updateFormData('streetAddress', e.target.value)}
                    placeholder="12 rue de la République"
                    data-testid="input-street-address"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="nbRooms" className="text-slate-700 mb-2 block">
                      Nombre de pièces
                    </Label>
                    <select
                      id="nbRooms"
                      value={formData.nbRooms}
                      onChange={(e) => updateFormData('nbRooms', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                      data-testid="select-rooms"
                    >
                      <option value="1">1 pièce (Studio)</option>
                      <option value="2">2 pièces (T2)</option>
                      <option value="3">3 pièces (T3)</option>
                      <option value="4">4 pièces (T4)</option>
                      <option value="5">5 pièces et +</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="renovationCost" className="text-slate-700 mb-2 block">
                      Travaux prévus (€)
                    </Label>
                    <div className="relative">
                      <Wrench className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="renovationCost"
                        type="number"
                        value={formData.renovationCost}
                        onChange={(e) => updateFormData('renovationCost', e.target.value)}
                        placeholder="0"
                        className="pl-10"
                        data-testid="input-renovation"
                      />
                    </div>
                  </div>
                </div>

                {/* Airbnb capacity fields */}
                <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Home className="h-5 w-5 text-rose-600" />
                    <h4 className="font-medium text-slate-800">Capacité d'accueil Airbnb</h4>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nbBedrooms" className="text-slate-700 mb-2 block text-sm">
                        Nombre de chambres
                      </Label>
                      <select
                        id="nbBedrooms"
                        value={formData.nbBedrooms}
                        onChange={(e) => updateFormData('nbBedrooms', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                        data-testid="select-bedrooms"
                      >
                        <option value="0">Studio (0 chambre)</option>
                        <option value="1">1 chambre</option>
                        <option value="2">2 chambres</option>
                        <option value="3">3 chambres</option>
                        <option value="4">4+ chambres</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="nbGuests" className="text-slate-700 mb-2 block text-sm">
                        Capacité (couchages)
                      </Label>
                      <select
                        id="nbGuests"
                        value={formData.nbGuests}
                        onChange={(e) => updateFormData('nbGuests', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                        data-testid="select-guests"
                      >
                        <option value="2">2 personnes</option>
                        <option value="4">4 personnes</option>
                        <option value="6">6 personnes</option>
                        <option value="8">8+ personnes</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-rose-600 mt-3">
                    Ces informations permettent de comparer avec des annonces similaires sur Airbnb
                  </p>
                </div>

                {/* DPE & Diagnostics */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-5 w-5 text-amber-600" />
                    <h4 className="font-medium text-slate-800">Diagnostics immobiliers</h4>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label htmlFor="dpe" className="text-slate-700 mb-2 block text-sm">
                        DPE (Performance énergétique)
                      </Label>
                      <select
                        id="dpe"
                        value={formData.dpe}
                        onChange={(e) => updateFormData('dpe', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                        data-testid="select-dpe"
                      >
                        <option value="A">A - Excellent</option>
                        <option value="B">B - Très bon</option>
                        <option value="C">C - Bon</option>
                        <option value="D">D - Moyen</option>
                        <option value="E">E - Passable</option>
                        <option value="F">F - Mauvais</option>
                        <option value="G">G - Très mauvais</option>
                      </select>
                      {parseFloat(formData.surface) < 50 && ['F', 'G'].includes(formData.dpe) && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          ✓ DPE non bloquant (surface &lt; 50m²)
                        </p>
                      )}
                      {parseFloat(formData.surface) >= 50 && ['F', 'G'].includes(formData.dpe) && (
                        <p className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ Interdiction de location à partir de 2028 (F) / 2025 (G)
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="electricityDiag" className="text-slate-700 mb-2 block text-sm">
                        Diagnostic électricité
                      </Label>
                      <select
                        id="electricityDiag"
                        value={formData.electricityDiag}
                        onChange={(e) => updateFormData('electricityDiag', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                        data-testid="select-electricity"
                      >
                        <option value="conforme">✅ Conforme</option>
                        <option value="anomalies">⚠️ Anomalies mineures</option>
                        <option value="non_conforme">❌ Non conforme</option>
                      </select>
                      {formData.electricityDiag === 'non_conforme' && (
                        <p className="text-xs text-red-600 mt-1">Travaux obligatoires</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="sanitationDiag" className="text-slate-700 mb-2 block text-sm">
                        Assainissement (eaux usées)
                      </Label>
                      <select
                        id="sanitationDiag"
                        value={formData.sanitationDiag}
                        onChange={(e) => updateFormData('sanitationDiag', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900"
                        data-testid="select-sanitation"
                      >
                        <option value="conforme">✅ Conforme</option>
                        <option value="anomalies">⚠️ Anomalies mineures</option>
                        <option value="non_conforme">❌ Non conforme</option>
                      </select>
                      {formData.sanitationDiag === 'non_conforme' && (
                        <p className="text-xs text-red-600 mt-1">Mise en conformité requise</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contacts professionnels */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-slate-600" />
                    <h4 className="font-medium text-slate-800">Contacts professionnels (optionnel)</h4>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Agent immobilier */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-600">Agent immobilier</p>
                      <Input
                        placeholder="Nom de l'agent"
                        value={formData.agentName}
                        onChange={(e) => updateFormData('agentName', e.target.value)}
                        data-testid="input-agent-name"
                      />
                      <Input
                        placeholder="Téléphone"
                        value={formData.agentPhone}
                        onChange={(e) => updateFormData('agentPhone', e.target.value)}
                        data-testid="input-agent-phone"
                      />
                      <Input
                        type="email"
                        placeholder="Email"
                        value={formData.agentEmail}
                        onChange={(e) => updateFormData('agentEmail', e.target.value)}
                        data-testid="input-agent-email"
                      />
                    </div>
                    
                    {/* Notaire */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-600">Notaire</p>
                      <Input
                        placeholder="Nom du notaire"
                        value={formData.notaryName}
                        onChange={(e) => updateFormData('notaryName', e.target.value)}
                        data-testid="input-notary-name"
                      />
                      <Input
                        placeholder="Nom de l'étude"
                        value={formData.notaryOffice}
                        onChange={(e) => updateFormData('notaryOffice', e.target.value)}
                        data-testid="input-notary-office"
                      />
                      <Input
                        placeholder="Téléphone"
                        value={formData.notaryPhone}
                        onChange={(e) => updateFormData('notaryPhone', e.target.value)}
                        data-testid="input-notary-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-2">
                    <Info className="h-4 w-4" />
                    <span className="text-sm font-medium">Frais de notaire estimés</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">
                    {formData.notaryFees ? formatCurrency(parseFloat(formData.notaryFees)) : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">~8% du prix d'acquisition (ancien)</p>
                </div>
              </div>
              
              {/* Dates clés du projet */}
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <h4 className="font-medium text-purple-800">Dates clés du projet (optionnel)</h4>
                </div>
                <p className="text-sm text-purple-600 mb-4">Ces dates seront incluses dans votre dossier bancaire pour plus de crédibilité</p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="dateOffreAchat" className="text-slate-700 text-sm mb-1 block">
                      Date signature offre d'achat
                    </Label>
                    <Input
                      type="date"
                      id="dateOffreAchat"
                      value={formData.dateOffreAchat}
                      onChange={(e) => updateFormData('dateOffreAchat', e.target.value)}
                      className="bg-white"
                      data-testid="date-offre-achat"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="datePromesseVente" className="text-slate-700 text-sm mb-1 block">
                      Date signature promesse de vente
                    </Label>
                    <Input
                      type="date"
                      id="datePromesseVente"
                      value={formData.datePromesseVente}
                      onChange={(e) => updateFormData('datePromesseVente', e.target.value)}
                      className="bg-white"
                      data-testid="date-promesse-vente"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="dateAchatTheorique" className="text-slate-700 text-sm mb-1 block">
                      Date théorique acte authentique
                    </Label>
                    <Input
                      type="date"
                      id="dateAchatTheorique"
                      value={formData.dateAchatTheorique}
                      onChange={(e) => updateFormData('dateAchatTheorique', e.target.value)}
                      className="bg-white"
                      data-testid="date-achat-theorique"
                    />
                  </div>
                </div>
                
                {formData.dateAchatTheorique && (
                  <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>Premier loyer estimé :</strong> {new Date(new Date(formData.dateAchatTheorique).setMonth(new Date(formData.dateAchatTheorique).getMonth() + 1)).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Market Data */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-2-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Analyse du marché</h2>
                  <p className="text-sm text-slate-500">Données de marché pour {formData.city} ({formData.postalCode})</p>
                </div>
              </div>
              
              {marketDataLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent mb-4"></div>
                  <p className="text-slate-600">Récupération des données de marché...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <Button
                    onClick={fetchMarketData}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-fetch-market"
                  >
                    <BarChart3 className="mr-2 h-4 w-4" />
                    Analyser le marché de {formData.city}
                  </Button>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Airbnb Data */}
                    <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-6 border border-rose-200">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center">
                          <Home className="h-4 w-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-800">Location Airbnb</h3>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-slate-500">Prix moyen / nuit</p>
                          <p className="text-2xl font-bold text-slate-800">
                            {formData.airbnbPricePerNight ? formatCurrency(formData.airbnbPricePerNight) : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Taux d'occupation estimé</p>
                          <p className="text-lg font-semibold text-slate-700">{formData.airbnbOccupancyRate}%</p>
                        </div>
                      </div>
                      <p className="text-xs text-rose-600 mt-4">Source: Airbnb (temps réel)</p>
                    </div>

                    {/* Traditional Rental Data */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-800">Location Traditionnelle</h3>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-slate-500">Prix moyen / m²</p>
                          <p className="text-2xl font-bold text-slate-800">
                            {formData.traditionalRentPerM2 ? `${formData.traditionalRentPerM2} €/m²` : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Loyer estimé ({formData.surface} m²)</p>
                          <p className="text-lg font-semibold text-slate-700">
                            {formData.traditionalMonthlyRent ? formatCurrency(parseFloat(formData.traditionalMonthlyRent)) + '/mois' : '—'}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-4">Source: INSEE</p>
                    </div>
                  </div>

                  {formData.marketDataSource && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-800">{formData.marketDataSource}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Financing */}
          {currentStep === 3 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-3-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <PiggyBank className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Votre financement</h2>
                  <p className="text-sm text-slate-500">Configurez votre prêt immobilier</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-slate-700 mb-3 block">Type de financement</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => updateFormData('loanType', '100')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.loanType === '100' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="btn-loan-100"
                    >
                      <p className="font-semibold text-slate-800">Prêt 100%</p>
                      <p className="text-sm text-slate-500">Prix du bien uniquement</p>
                    </button>
                    <button
                      onClick={() => updateFormData('loanType', '110')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.loanType === '110' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="btn-loan-110"
                    >
                      <p className="font-semibold text-slate-800">Prêt 110%</p>
                      <p className="text-sm text-slate-500">Bien + frais de notaire</p>
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-3 block">Durée du prêt</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {['10', '15', '20', '25'].map(duration => (
                      <button
                        key={duration}
                        onClick={() => updateFormData('loanDuration', duration)}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.loanDuration === duration 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`btn-duration-${duration}`}
                      >
                        <p className="font-semibold text-slate-800">{duration} ans</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="interestRate" className="text-slate-700 mb-2 block">
                      Taux d'intérêt (%)
                    </Label>
                    <div className="relative">
                      <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="interestRate"
                        type="number"
                        step="0.01"
                        value={formData.interestRate}
                        onChange={(e) => updateFormData('interestRate', e.target.value)}
                        placeholder="3.50"
                        className="pl-10"
                        data-testid="input-interest-rate"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Taux marché actuel ~3.5%</p>
                  </div>
                  <div>
                    <Label htmlFor="insuranceRate" className="text-slate-700 mb-2 block">
                      Taux assurance (%)
                    </Label>
                    <div className="relative">
                      <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="insuranceRate"
                        type="number"
                        step="0.01"
                        value={formData.insuranceRate}
                        onChange={(e) => updateFormData('insuranceRate', e.target.value)}
                        placeholder="0.34"
                        className="pl-10"
                        data-testid="input-insurance-rate"
                      />
                    </div>
                  </div>
                </div>

                {/* Loan Summary */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Récapitulatif du prêt</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Montant emprunté</p>
                      <p className="text-xl font-bold text-slate-800">
                        {formData.propertyPrice ? formatCurrency(
                          parseFloat(formData.propertyPrice) + 
                          (parseFloat(formData.renovationCost) || 0) +
                          (formData.loanType === '110' ? (parseFloat(formData.notaryFees) || 0) : 0)
                        ) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Durée</p>
                      <p className="text-xl font-bold text-slate-800">{formData.loanDuration} ans</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Travaux détaillés */}
          {currentStep === 4 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-4-works-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Wrench className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Travaux & Équipement</h2>
                  <p className="text-sm text-slate-500">Détaillez les travaux et l'équipement nécessaire</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Upload Devis AI Analysis - Multi-devis */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      Importer vos devis travaux (IA)
                    </h3>
                    {analyzedDevis.length > 0 && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full" data-testid="devis-count">
                        {analyzedDevis.length} devis
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-blue-600 mb-3">
                    Uploadez les PDF de vos devis artisans. Chaque devis sera analysé et intégré au dossier bancaire.
                  </p>
                  
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer" data-testid="devis-upload-label">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleDevisUpload}
                        disabled={devisAnalyzing}
                        className="hidden"
                        data-testid="devis-upload-input"
                      />
                      <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 border-dashed transition-colors ${
                        devisAnalyzing 
                          ? 'border-blue-300 bg-blue-100 cursor-wait' 
                          : 'border-blue-300 hover:border-blue-500 hover:bg-blue-100'
                      }`}>
                        {devisAnalyzing ? (
                          <>
                            <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-blue-700 font-medium">Analyse IA en cours...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 text-blue-600" />
                            <span className="text-sm text-blue-700 font-medium">
                              {analyzedDevis.length > 0 ? 'Ajouter un autre devis PDF' : 'Choisir un devis PDF'}
                            </span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                  
                  {/* List of analyzed devis */}
                  {analyzedDevis.length > 0 && (
                    <div className="mt-4 space-y-3" data-testid="devis-list">
                      {analyzedDevis.map((devis) => (
                        <div key={devis.id} className="bg-white rounded-lg p-4 border border-blue-200" data-testid={`devis-item-${devis.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-600" />
                                {devis.company?.name || devis.filename}
                              </h4>
                              {devis.company?.name && (
                                <p className="text-xs text-slate-500 mt-0.5 ml-6">{devis.filename}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-blue-600">
                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(devis.totals?.total_ttc || 0)}
                              </span>
                              <button
                                onClick={() => removeDevis(devis.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors"
                                data-testid={`btn-remove-devis-${devis.id}`}
                              >
                                <X className="h-4 w-4 text-red-400 hover:text-red-600" />
                              </button>
                            </div>
                          </div>
                          
                          {devis.line_items?.length > 0 && (
                            <div className="space-y-1 ml-6 max-h-24 overflow-y-auto">
                              {devis.line_items.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="flex justify-between text-xs text-slate-500">
                                  <span className="truncate flex-1 mr-2">{item.description}</span>
                                  <span className="font-medium whitespace-nowrap">{new Intl.NumberFormat('fr-FR').format(item.amount_ttc || item.amount_ht || 0)} €</span>
                                </div>
                              ))}
                              {devis.line_items.length > 5 && (
                                <p className="text-xs text-slate-400">+ {devis.line_items.length - 5} autres postes</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Grand total + Apply button */}
                      <div className="bg-blue-100 rounded-lg p-4 border border-blue-300">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-blue-900">
                            Total {analyzedDevis.length} devis :
                          </span>
                          <span className="text-xl font-bold text-blue-700" data-testid="devis-grand-total">
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
                              analyzedDevis.reduce((sum, d) => sum + (d.totals?.total_ttc || 0), 0)
                            )} TTC
                          </span>
                        </div>
                        <Button
                          onClick={applyAllDevisToWorks}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm py-2.5"
                          data-testid="btn-apply-all-devis"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Appliquer tous les devis aux travaux
                        </Button>
                        <p className="text-xs text-blue-600 mt-2 text-center">
                          Les devis seront automatiquement inclus dans votre dossier bancaire
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Travaux détaillés */}
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                    <Paintbrush className="h-5 w-5" />
                    Travaux à prévoir
                  </h3>
                  
                  <div className="space-y-3">
                    {/* Électricité */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksElectricity}
                        onChange={(e) => {
                          updateFormData('worksElectricity', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksElectricityAmount', getWorksEstimate('worksElectricity', formData.surface).toString());
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Plug className="h-5 w-5 text-orange-600" />
                      <span className="flex-1 text-slate-700">Électricité</span>
                      <Input
                        type="number"
                        value={formData.worksElectricityAmount}
                        onChange={(e) => updateFormData('worksElectricityAmount', e.target.value)}
                        disabled={!formData.worksElectricity}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Plomberie */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksPlumbing}
                        onChange={(e) => {
                          updateFormData('worksPlumbing', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksPlumbingAmount', getWorksEstimate('worksPlumbing', formData.surface).toString());
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Droplets className="h-5 w-5 text-blue-600" />
                      <span className="flex-1 text-slate-700">Plomberie</span>
                      <Input
                        type="number"
                        value={formData.worksPlumbingAmount}
                        onChange={(e) => updateFormData('worksPlumbingAmount', e.target.value)}
                        disabled={!formData.worksPlumbing}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Toiture */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksRoof}
                        onChange={(e) => {
                          updateFormData('worksRoof', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksRoofAmount', getWorksEstimate('worksRoof', formData.surface).toString());
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Home className="h-5 w-5 text-slate-600" />
                      <span className="flex-1 text-slate-700">Toiture / Façade</span>
                      <Input
                        type="number"
                        value={formData.worksRoofAmount}
                        onChange={(e) => updateFormData('worksRoofAmount', e.target.value)}
                        disabled={!formData.worksRoof}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Intérieur */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksInterior}
                        onChange={(e) => {
                          updateFormData('worksInterior', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksInteriorAmount', getWorksEstimate('worksInterior', formData.surface).toString());
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Paintbrush className="h-5 w-5 text-purple-600" />
                      <span className="flex-1 text-slate-700">Rénovation intérieure</span>
                      <Input
                        type="number"
                        value={formData.worksInteriorAmount}
                        onChange={(e) => updateFormData('worksInteriorAmount', e.target.value)}
                        disabled={!formData.worksInterior}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Isolation */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksInsulation}
                        onChange={(e) => {
                          updateFormData('worksInsulation', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksInsulationAmount', getWorksEstimate('worksInsulation', formData.surface).toString());
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Zap className="h-5 w-5 text-green-600" />
                      <span className="flex-1 text-slate-700">Isolation</span>
                      <Input
                        type="number"
                        value={formData.worksInsulationAmount}
                        onChange={(e) => updateFormData('worksInsulationAmount', e.target.value)}
                        disabled={!formData.worksInsulation}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Autres */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.worksOther}
                        onChange={(e) => {
                          updateFormData('worksOther', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('worksOtherAmount', '1000');
                          }
                        }}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <Wrench className="h-5 w-5 text-slate-600" />
                      <span className="flex-1 text-slate-700">Autres travaux</span>
                      <Input
                        type="number"
                        value={formData.worksOtherAmount}
                        onChange={(e) => updateFormData('worksOtherAmount', e.target.value)}
                        disabled={!formData.worksOther}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-orange-200 flex justify-between items-center">
                    <span className="font-semibold text-orange-800">Total travaux :</span>
                    <span className="text-2xl font-bold text-orange-600">{formatCurrency(calculateTotalWorks())}</span>
                  </div>
                </div>
                
                {/* Équipement Airbnb */}
                <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-rose-800 flex items-center gap-2">
                      <Sofa className="h-5 w-5" />
                      Équipement Airbnb / meublé
                    </h3>
                    
                    {/* Slider Occasion / Neuf */}
                    <div className="flex items-center gap-2 bg-white rounded-full p-1">
                      <button
                        onClick={() => updateFormData('airbnbEquipmentLevel', 'occasion')}
                        data-testid="equip-level-occasion"
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          formData.airbnbEquipmentLevel === 'occasion' 
                            ? 'bg-rose-600 text-white' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        Occasion (LBC)
                      </button>
                      <button
                        onClick={() => updateFormData('airbnbEquipmentLevel', 'neuf')}
                        data-testid="equip-level-neuf"
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          formData.airbnbEquipmentLevel === 'neuf' 
                            ? 'bg-rose-600 text-white' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        Neuf (But/Electrodepot)
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Cuisine meubles */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipKitchenFurniture}
                        data-testid="checkbox-equip-kitchen"
                        onChange={(e) => {
                          updateFormData('equipKitchenFurniture', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipKitchenFurnitureAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipKitchenFurniture.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <ChefHat className="h-5 w-5 text-rose-600" />
                      <span className="flex-1 text-slate-700">Meubles de cuisine</span>
                      <Input
                        type="number"
                        value={formData.equipKitchenFurnitureAmount}
                        onChange={(e) => updateFormData('equipKitchenFurnitureAmount', e.target.value)}
                        disabled={!formData.equipKitchenFurniture}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* SDB */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipBathroomRenovation}
                        data-testid="checkbox-equip-bathroom"
                        onChange={(e) => {
                          updateFormData('equipBathroomRenovation', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipBathroomRenovationAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipBathroomRenovation.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <Droplets className="h-5 w-5 text-blue-600" />
                      <span className="flex-1 text-slate-700">Réfection salle de bains</span>
                      <Input
                        type="number"
                        value={formData.equipBathroomRenovationAmount}
                        onChange={(e) => updateFormData('equipBathroomRenovationAmount', e.target.value)}
                        disabled={!formData.equipBathroomRenovation}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Chauffe-eau */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipWaterHeater}
                        data-testid="checkbox-equip-water-heater"
                        onChange={(e) => {
                          updateFormData('equipWaterHeater', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipWaterHeaterAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipWaterHeater.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <Zap className="h-5 w-5 text-amber-600" />
                      <span className="flex-1 text-slate-700">Chauffe-eau</span>
                      <Input
                        type="number"
                        value={formData.equipWaterHeaterAmount}
                        onChange={(e) => updateFormData('equipWaterHeaterAmount', e.target.value)}
                        disabled={!formData.equipWaterHeater}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Électroménager */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipAppliances}
                        data-testid="checkbox-equip-appliances"
                        onChange={(e) => {
                          updateFormData('equipAppliances', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipAppliancesAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipAppliances.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <Plug className="h-5 w-5 text-slate-600" />
                      <span className="flex-1 text-slate-700">Électroménager</span>
                      <Input
                        type="number"
                        value={formData.equipAppliancesAmount}
                        onChange={(e) => updateFormData('equipAppliancesAmount', e.target.value)}
                        disabled={!formData.equipAppliances}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Mobilier */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipFurniture}
                        data-testid="checkbox-equip-furniture"
                        onChange={(e) => {
                          updateFormData('equipFurniture', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipFurnitureAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipFurniture.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <Sofa className="h-5 w-5 text-indigo-600" />
                      <span className="flex-1 text-slate-700">Mobilier (lit, canapé, table...)</span>
                      <Input
                        type="number"
                        value={formData.equipFurnitureAmount}
                        onChange={(e) => updateFormData('equipFurnitureAmount', e.target.value)}
                        disabled={!formData.equipFurniture}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                    
                    {/* Trousseau */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.equipLinenKit}
                        data-testid="checkbox-equip-linen"
                        onChange={(e) => {
                          updateFormData('equipLinenKit', e.target.checked);
                          if (e.target.checked) {
                            updateFormData('equipLinenKitAmount', EQUIPMENT_PRICES[formData.airbnbEquipmentLevel].equipLinenKit.toString());
                          }
                        }}
                        className="w-5 h-5 accent-rose-600"
                      />
                      <Home className="h-5 w-5 text-teal-600" />
                      <span className="flex-1 text-slate-700">Trousseau (linge, vaisselle...)</span>
                      <Input
                        type="number"
                        value={formData.equipLinenKitAmount}
                        onChange={(e) => updateFormData('equipLinenKitAmount', e.target.value)}
                        disabled={!formData.equipLinenKit}
                        className="w-28 text-right"
                        placeholder="0"
                      />
                      <span className="text-slate-500">€</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-rose-200 flex justify-between items-center">
                    <span className="font-semibold text-rose-800">Total équipement :</span>
                    <span className="text-2xl font-bold text-rose-600">{formatCurrency(calculateTotalEquipment())}</span>
                  </div>
                </div>
                
                {/* Récapitulatif */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-blue-800">Budget total travaux + équipement :</span>
                    <span className="text-3xl font-bold text-blue-600">{formatCurrency(calculateTotalWorks() + calculateTotalEquipment())}</span>
                  </div>
                </div>
                
                {/* Documents justificatifs travaux */}
                <div className="mt-6 bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="h-6 w-6 text-amber-600" />
                    <div>
                      <h3 className="font-semibold text-amber-800">Documents justificatifs pour la banque</h3>
                      <p className="text-sm text-amber-600">Photos des zones à rénover + devis artisans (PDF)</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Upload Photo */}
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <Label className="text-slate-700 mb-2 block">Photo (salle de bain, cuisine...)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const tempDoc = {
                                id: `temp_${Date.now()}`,
                                type: 'photo',
                                label: '',
                                description: '',
                                file: file,
                                preview: URL.createObjectURL(file)
                              };
                              updateFormData('workDocuments', [...formData.workDocuments, tempDoc]);
                            }
                          }}
                          className="flex-1"
                          data-testid="work-photo-input"
                        />
                      </div>
                    </div>
                    
                    {/* Upload PDF */}
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <Label className="text-slate-700 mb-2 block">Devis artisan (PDF)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const tempDoc = {
                                id: `temp_${Date.now()}`,
                                type: 'pdf',
                                label: '',
                                description: '',
                                file: file,
                                filename: file.name
                              };
                              updateFormData('workDocuments', [...formData.workDocuments, tempDoc]);
                            }
                          }}
                          className="flex-1"
                          data-testid="work-pdf-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Liste des documents ajoutés */}
                  {formData.workDocuments.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-amber-800">{formData.workDocuments.length} document(s) ajouté(s) :</p>
                      {formData.workDocuments.map((doc, index) => (
                        <div key={doc.id} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-slate-200">
                          {doc.type === 'photo' ? (
                            <img src={doc.preview} alt="Preview" className="w-16 h-16 object-cover rounded" />
                          ) : (
                            <div className="w-16 h-16 bg-red-100 rounded flex items-center justify-center">
                              <FileText className="h-8 w-8 text-red-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <Input
                              type="text"
                              placeholder={doc.type === 'photo' ? "Ex: Salle de bain à refaire" : "Ex: Devis électricité"}
                              value={doc.label}
                              onChange={(e) => {
                                const updated = [...formData.workDocuments];
                                updated[index].label = e.target.value;
                                updateFormData('workDocuments', updated);
                              }}
                              className="mb-2"
                            />
                            <Input
                              type="text"
                              placeholder="Description (optionnel)"
                              value={doc.description}
                              onChange={(e) => {
                                const updated = [...formData.workDocuments];
                                updated[index].description = e.target.value;
                                updateFormData('workDocuments', updated);
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div className="flex flex-col gap-1 text-right">
                            <span className="text-xs text-slate-500">{doc.type === 'photo' ? 'Photo' : 'PDF'}</span>
                            {doc.filename && <span className="text-xs text-slate-400 truncate max-w-[100px]">{doc.filename}</span>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = formData.workDocuments.filter((_, i) => i !== index);
                              updateFormData('workDocuments', updated);
                            }}
                            className="text-red-500 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-amber-600 mt-3">
                    📎 Max 20 documents • Photos : JPG, PNG, WebP (5Mo max) • Devis : PDF (10Mo max)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Rental Scenarios */}
          {currentStep === 5 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-5-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Home className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Scénarios de location</h2>
                  <p className="text-sm text-slate-500">Configurez vos hypothèses de location</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-slate-700 mb-3 block">Type de location à simuler</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      onClick={() => updateFormData('rentalType', 'traditional')}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        formData.rentalType === 'traditional' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Building2 className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                      <p className="font-semibold text-slate-800">Longue durée</p>
                    </button>
                    <button
                      onClick={() => updateFormData('rentalType', 'airbnb')}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        formData.rentalType === 'airbnb' 
                          ? 'border-rose-500 bg-rose-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Home className="h-6 w-6 mx-auto mb-2 text-rose-600" />
                      <p className="font-semibold text-slate-800">LMNP / Airbnb</p>
                    </button>
                    <button
                      onClick={() => updateFormData('rentalType', 'both')}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        formData.rentalType === 'both' 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <BarChart3 className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                      <p className="font-semibold text-slate-800">Comparer</p>
                    </button>
                  </div>
                </div>

                {/* Traditional Rental Config */}
                {(formData.rentalType === 'traditional' || formData.rentalType === 'both') && (
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Location longue durée
                    </h3>
                    <div>
                      <Label htmlFor="traditionalMonthlyRent" className="text-slate-700 mb-2 block">
                        Loyer mensuel (€)
                      </Label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="traditionalMonthlyRent"
                          type="number"
                          value={formData.traditionalMonthlyRent}
                          onChange={(e) => updateFormData('traditionalMonthlyRent', e.target.value)}
                          placeholder="800"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Airbnb Rental Config */}
                {(formData.rentalType === 'airbnb' || formData.rentalType === 'both') && (
                  <div className="bg-rose-50 rounded-lg p-6 border border-rose-200">
                    <h3 className="font-semibold text-rose-800 mb-4 flex items-center gap-2">
                      <Home className="h-5 w-5" />
                      Location LMNP / Airbnb
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="airbnbNightlyRate" className="text-slate-700 mb-2 block">
                          Prix par nuit (€)
                        </Label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="airbnbNightlyRate"
                            type="number"
                            value={formData.airbnbNightlyRate}
                            onChange={(e) => updateFormData('airbnbNightlyRate', e.target.value)}
                            placeholder="80"
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="airbnbDaysPerYear" className="text-slate-700 mb-2 block">
                          Jours loués / an
                        </Label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="airbnbDaysPerYear"
                            type="number"
                            value={formData.airbnbDaysPerYear}
                            onChange={(e) => updateFormData('airbnbDaysPerYear', e.target.value)}
                            placeholder="120"
                            className="pl-10"
                            max="365"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Max légal zone tendue: 120 jours</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Charges & Taxes */}
          {currentStep === 6 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-6-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Scale className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Charges & Fiscalité</h2>
                  <p className="text-sm text-slate-500">Paramétrez vos charges et votre situation fiscale</p>
                </div>
              </div>
              
              <div className="space-y-8">
                {/* Charges Section */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-4">Charges annuelles</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="propertyTax" className="text-slate-700 mb-2 block">
                        Taxe foncière (€/an) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="propertyTax"
                        type="number"
                        value={formData.propertyTax}
                        onChange={(e) => updateFormData('propertyTax', e.target.value)}
                        placeholder="800"
                      />
                    </div>
                    <div>
                      <Label htmlFor="insurancePNO" className="text-slate-700 mb-2 block">
                        Assurance PNO (€/an)
                      </Label>
                      <Input
                        id="insurancePNO"
                        type="number"
                        value={formData.insurancePNO}
                        onChange={(e) => updateFormData('insurancePNO', e.target.value)}
                        placeholder="300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="managementFees" className="text-slate-700 mb-2 block">
                        Frais de gestion (€/an)
                      </Label>
                      <Input
                        id="managementFees"
                        type="number"
                        value={formData.managementFees}
                        onChange={(e) => updateFormData('managementFees', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="maintenanceProvision" className="text-slate-700 mb-2 block">
                        Provision travaux (€/an)
                      </Label>
                      <Input
                        id="maintenanceProvision"
                        type="number"
                        value={formData.maintenanceProvision}
                        onChange={(e) => updateFormData('maintenanceProvision', e.target.value)}
                        placeholder="500"
                      />
                    </div>
                  </div>
                </div>

                {/* Airbnb specific charges */}
                {(formData.rentalType === 'airbnb' || formData.rentalType === 'both') && (
                  <div className="bg-rose-50 rounded-lg p-6 border border-rose-200">
                    <h3 className="font-semibold text-rose-800 mb-4">Charges spécifiques Airbnb</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="airbnbCleaningFee" className="text-slate-700 mb-2 block">
                          Ménage par rotation (€)
                        </Label>
                        <Input
                          id="airbnbCleaningFee"
                          type="number"
                          value={formData.airbnbCleaningFee}
                          onChange={(e) => updateFormData('airbnbCleaningFee', e.target.value)}
                          placeholder="50"
                        />
                      </div>
                      <div>
                        <Label htmlFor="airbnbPlatformFee" className="text-slate-700 mb-2 block">
                          Commission plateforme (%)
                        </Label>
                        <Input
                          id="airbnbPlatformFee"
                          type="number"
                          value={formData.airbnbPlatformFee}
                          onChange={(e) => updateFormData('airbnbPlatformFee', e.target.value)}
                          placeholder="3"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Tax Section */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-4">Situation fiscale</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rfr" className="text-slate-700 mb-2 block">
                        Revenu Fiscal de Référence (€)
                      </Label>
                      <Input
                        id="rfr"
                        type="number"
                        value={formData.rfr}
                        onChange={(e) => updateFormData('rfr', e.target.value)}
                        placeholder="40000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="taxParts" className="text-slate-700 mb-2 block">
                        Nombre de parts fiscales
                      </Label>
                      <select
                        id="taxParts"
                        value={formData.taxParts}
                        onChange={(e) => updateFormData('taxParts', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="1">1 part (célibataire)</option>
                        <option value="1.5">1.5 parts</option>
                        <option value="2">2 parts (couple)</option>
                        <option value="2.5">2.5 parts</option>
                        <option value="3">3 parts</option>
                        <option value="3.5">3.5 parts</option>
                        <option value="4">4 parts</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label className="text-slate-700 mb-3 block">Régime fiscal</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => updateFormData('taxRegime', 'micro-bic')}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.taxRegime === 'micro-bic' 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-800">Micro-BIC</p>
                        <p className="text-sm text-slate-500">Abattement 50% (LMNP)</p>
                      </button>
                      <button
                        onClick={() => updateFormData('taxRegime', 'reel')}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.taxRegime === 'reel' 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-800">Réel simplifié</p>
                        <p className="text-sm text-slate-500">Déduction des charges réelles</p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Exit Strategy (Revente) */}
          {currentStep === 7 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-7-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Stratégie de sortie</h2>
                  <p className="text-sm text-slate-500">Simulez la revente et la plus-value</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-slate-700 mb-3 block">Durée de détention prévue</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {['5', '10', '15', '20'].map(duration => (
                      <button
                        key={duration}
                        onClick={() => updateFormData('holdingDuration', duration)}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.holdingDuration === duration 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-800">{duration} ans</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="expectedAppreciation" className="text-slate-700 mb-2 block">
                    Appréciation annuelle estimée (%)
                  </Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="expectedAppreciation"
                      type="number"
                      step="0.1"
                      value={formData.expectedAppreciation}
                      onChange={(e) => updateFormData('expectedAppreciation', e.target.value)}
                      placeholder="2"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Moyenne historique en France: ~2-3% par an</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isMainResidence}
                      onChange={(e) => updateFormData('isMainResidence', e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <p className="font-medium text-slate-800">Résidence principale à la revente</p>
                      <p className="text-sm text-slate-500">Exonération de la taxe sur la plus-value</p>
                    </div>
                  </label>
                </div>

                {/* Preview calculations */}
                <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-4">Estimation de revente</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Prix de revente estimé</p>
                      <p className="text-xl font-bold text-slate-800">
                        {formData.propertyPrice ? formatCurrency(
                          parseFloat(formData.propertyPrice) * 
                          Math.pow(1 + parseFloat(formData.expectedAppreciation) / 100, parseInt(formData.holdingDuration))
                        ) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Plus-value brute</p>
                      <p className="text-xl font-bold text-green-600">
                        {formData.propertyPrice ? formatCurrency(
                          parseFloat(formData.propertyPrice) * 
                          Math.pow(1 + parseFloat(formData.expectedAppreciation) / 100, parseInt(formData.holdingDuration)) -
                          parseFloat(formData.propertyPrice)
                        ) : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 8: Photos du bien */}
          {currentStep === 8 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-8-photos-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Camera className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Photos du bien</h2>
                  <p className="text-sm text-slate-500">Ajoutez jusqu'à 10 photos pour illustrer le dossier PDF</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {PHOTO_PRESETS.map((preset) => {
                  const photo = formData.photos.find(p => p.id === preset.id);
                  
                  return (
                    <div key={preset.id} className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        id={`photo-${preset.id}`}
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(preset.id, e.target.files[0])}
                      />
                      
                      {photo ? (
                        <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-teal-400">
                          <img 
                            src={photo.data} 
                            alt={preset.label}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removePhoto(preset.id)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center truncate">
                            {preset.label}
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`photo-${preset.id}`}
                          className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-colors"
                        >
                          <Upload className="h-8 w-8 text-slate-400 mb-2" />
                          <span className="text-xs text-slate-500 text-center px-2">{preset.label}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  <strong>{formData.photos.length}</strong> photo(s) ajoutée(s) sur 10 maximum.
                  Ces photos seront incluses dans le dossier PDF pour illustrer votre investissement.
                </p>
              </div>
            </div>
          )}

          {/* Step 9: Results */}
          {currentStep === 9 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-9-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Résultats & Recommandation</h2>
                  <p className="text-sm text-slate-500">Analyse complète de votre investissement</p>
                </div>
              </div>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent mb-4"></div>
                  <p className="text-slate-600">Calcul en cours...</p>
                </div>
              ) : formData.results ? (
                <div className="space-y-6">
                  {/* Recommendation */}
                  <div className={`rounded-xl p-6 ${
                    formData.results.recommendation === 'airbnb' 
                      ? 'bg-rose-50 border border-rose-200' 
                      : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle2 className={`h-6 w-6 ${
                        formData.results.recommendation === 'airbnb' ? 'text-rose-600' : 'text-blue-600'
                      }`} />
                      <h3 className="text-lg font-bold text-slate-800">Notre recommandation</h3>
                    </div>
                    <p className="text-slate-700">
                      {formData.results.recommendation === 'airbnb' 
                        ? 'La location courte durée (LMNP/Airbnb) semble plus rentable pour ce bien.'
                        : 'La location longue durée traditionnelle semble plus adaptée à ce bien.'
                      }
                    </p>
                  </div>

                  {/* Loan summary */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <PiggyBank className="h-5 w-5 text-slate-600" />
                      Récapitulatif du financement
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Montant emprunté</p>
                        <p className="font-bold text-slate-800">{formatCurrency(formData.results.loan_amount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Mensualité</p>
                        <p className="font-bold text-slate-800">{formatCurrency(formData.results.monthly_payment || 0)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Taux</p>
                        <p className="font-bold text-slate-800">{formData.results.interest_rate || formData.interestRate}%</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Durée</p>
                        <p className="font-bold text-slate-800">{formData.results.loan_duration || formData.loanDuration} ans</p>
                      </div>
                    </div>
                  </div>

                  {/* Charges breakdown */}
                  {formData.results.charges_detail && (
                    <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                      <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                        <Scale className="h-5 w-5" />
                        Détail des charges annuelles
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Taxe foncière</p>
                          <p className="font-bold text-slate-800">{formatCurrency(formData.results.charges_detail.property_tax_annual || 0)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Assurance PNO</p>
                          <p className="font-bold text-slate-800">{formatCurrency(formData.results.charges_detail.insurance_pno_annual || 0)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Provision travaux</p>
                          <p className="font-bold text-slate-800">{formatCurrency(formData.results.charges_detail.maintenance_annual || 0)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Total mensuel</p>
                          <p className="font-bold text-orange-700">{formatCurrency(formData.results.charges_detail.total_monthly || 0)}/mois</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cash-flow comparison with details */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Traditional */}
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                      <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        Location Traditionnelle
                      </h3>
                      <div className="space-y-3">
                        {formData.results.traditional_detail && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Loyer mensuel</span>
                              <span className="font-medium text-slate-800">+{formatCurrency(formData.results.traditional_detail.monthly_rent || 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Charges mensuelles</span>
                              <span className="font-medium text-red-600">-{formatCurrency(formData.results.traditional_detail.monthly_charges || 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Mensualité crédit</span>
                              <span className="font-medium text-red-600">-{formatCurrency(formData.results.traditional_detail.monthly_loan || 0)}</span>
                            </div>
                            <div className="border-t border-blue-200 pt-3 mt-3"></div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">Cash-flow mensuel</span>
                          <span className={`font-bold text-lg ${formData.results.traditional_cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(formData.results.traditional_cashflow || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Rendement brut</span>
                          <span className="font-bold text-slate-800">{formData.results.traditional_yield || 0}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Airbnb */}
                    <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
                      <h3 className="font-semibold text-rose-800 mb-4 flex items-center gap-2">
                        <Home className="h-5 w-5" />
                        Location Airbnb / LMNP
                      </h3>
                      <div className="space-y-3">
                        {formData.results.airbnb_detail && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Revenu brut ({formData.results.airbnb_detail.days_per_year || 0} nuits)</span>
                              <span className="font-medium text-slate-800">+{formatCurrency((formData.results.airbnb_detail.gross_annual || 0) / 12)}/mois</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Commission plateforme</span>
                              <span className="font-medium text-red-600">-{formatCurrency((formData.results.airbnb_detail.platform_fees_annual || 0) / 12)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Ménage + consommables</span>
                              <span className="font-medium text-red-600">-{formatCurrency(((formData.results.airbnb_detail.cleaning_costs_annual || 0) + (formData.results.airbnb_detail.consumables_annual || 0)) / 12)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Charges + crédit</span>
                              <span className="font-medium text-red-600">-{formatCurrency((formData.results.charges_detail?.total_monthly || 0) + (formData.results.monthly_payment || 0))}</span>
                            </div>
                            <div className="border-t border-rose-200 pt-3 mt-3"></div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">Cash-flow mensuel</span>
                          <span className={`font-bold text-lg ${formData.results.airbnb_cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(formData.results.airbnb_cashflow || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Rendement brut</span>
                          <span className="font-bold text-slate-800">{formData.results.airbnb_yield || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Exit strategy with OPERATION BALANCE */}
                  <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                    <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Stratégie de sortie ({formData.holdingDuration} ans)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-slate-500">Prix de revente</p>
                        <p className="text-xl font-bold text-slate-800">{formatCurrency(formData.results.resale_price || 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Plus-value nette</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(formData.results.net_capital_gain || 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Capital remboursé</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(formData.results.capital_repaid || 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Reste à rembourser</p>
                        <p className="text-xl font-bold text-slate-600">{formatCurrency(formData.results.remaining_loan || 0)}</p>
                      </div>
                    </div>

                    {/* SOLDE DE L'OPÉRATION */}
                    {formData.results.exit_strategy && (
                      <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-6 border-2 border-green-400">
                        <h4 className="font-bold text-green-800 mb-4 text-center text-lg">
                          💰 SOLDE NET DE L'OPÉRATION (si revente à {formData.holdingDuration} ans)
                        </h4>
                        <p className="text-center text-sm text-slate-600 mb-4">
                          = Prix de revente - Reste du prêt + Cash-flow cumulé - Impôt sur plus-value
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Traditional balance */}
                          <div className="text-center bg-white rounded-lg p-4">
                            <p className="text-sm text-slate-500 mb-1">Location Traditionnelle</p>
                            <p className="text-sm text-slate-600 mb-2">
                              Cash-flow cumulé: {formatCurrency(formData.results.exit_strategy.traditional_cumulative_cashflow || 0)}
                            </p>
                            <p className={`text-3xl font-bold ${(formData.results.exit_strategy.traditional_operation_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(formData.results.exit_strategy.traditional_operation_balance || 0)}
                            </p>
                          </div>
                          {/* Airbnb balance */}
                          <div className="text-center bg-white rounded-lg p-4">
                            <p className="text-sm text-slate-500 mb-1">Location Airbnb</p>
                            <p className="text-sm text-slate-600 mb-2">
                              Cash-flow cumulé: {formatCurrency(formData.results.exit_strategy.airbnb_cumulative_cashflow || 0)}
                            </p>
                            <p className={`text-3xl font-bold ${(formData.results.exit_strategy.airbnb_operation_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(formData.results.exit_strategy.airbnb_operation_balance || 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ARGUMENTAIRE BANCAIRE */}
                  {formData.results.banking_argument && (
                    <div className={`rounded-xl p-6 border-2 ${
                      formData.results.banking_argument.verdict_type === 'positive' 
                        ? 'bg-green-50 border-green-400' 
                        : formData.results.banking_argument.verdict_type === 'negative'
                          ? 'bg-red-50 border-red-400'
                          : 'bg-yellow-50 border-yellow-400'
                    }`}>
                      <h3 className="font-bold text-xl text-slate-800 mb-4">
                        📋 Argumentaire pour votre banquier
                      </h3>
                      <p className={`text-lg font-semibold mb-6 ${
                        formData.results.banking_argument.verdict_type === 'positive' 
                          ? 'text-green-700' 
                          : formData.results.banking_argument.verdict_type === 'negative'
                            ? 'text-red-700'
                            : 'text-yellow-700'
                      }`}>
                        {formData.results.banking_argument.verdict}
                      </p>
                      
                      {/* Arguments positifs */}
                      {formData.results.banking_argument.arguments?.length > 0 && (
                        <div className="space-y-4 mb-6">
                          <h4 className="font-semibold text-slate-700">Points forts du projet :</h4>
                          {formData.results.banking_argument.arguments.map((arg, idx) => (
                            <div key={idx} className="flex gap-3 bg-white rounded-lg p-4 border border-slate-200">
                              <span className="text-2xl">{arg.icon}</span>
                              <div>
                                <p className="font-semibold text-slate-800">{arg.title}</p>
                                <p className="text-sm text-slate-600">{arg.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Avertissements */}
                      {formData.results.banking_argument.warnings?.length > 0 && (
                        <div className="space-y-4">
                          <h4 className="font-semibold text-slate-700">Points de vigilance :</h4>
                          {formData.results.banking_argument.warnings.map((warn, idx) => (
                            <div key={idx} className="flex gap-3 bg-white rounded-lg p-4 border border-orange-200">
                              <span className="text-2xl">{warn.icon}</span>
                              <div>
                                <p className="font-semibold text-orange-800">{warn.title}</p>
                                <p className="text-sm text-slate-600">{warn.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* COMPARAISON LOCATION VS ACHAT (si projet non rentable en locatif) */}
                  {formData.results.rent_vs_buy && formData.results.banking_argument?.verdict_type === 'negative' && (
                    <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-300">
                      <h3 className="font-bold text-xl text-blue-800 mb-4">
                        🏠 Alternative : Achat en Résidence Principale
                      </h3>
                      <p className="text-slate-700 mb-4">{formData.results.rent_vs_buy.summary}</p>
                      
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-white rounded-lg p-4 text-center">
                          <p className="text-sm text-slate-500">Loyer équivalent</p>
                          <p className="text-xl font-bold text-slate-800">{formatCurrency(formData.results.rent_vs_buy.monthly_rent || 0)}/mois</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <p className="text-sm text-slate-500">Mensualité crédit</p>
                          <p className="text-xl font-bold text-slate-800">{formatCurrency(formData.results.rent_vs_buy.monthly_mortgage || 0)}/mois</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <p className="text-sm text-slate-500">Point de rentabilité</p>
                          <p className="text-xl font-bold text-blue-600">
                            {formData.results.rent_vs_buy.breakeven_year 
                              ? `${formData.results.rent_vs_buy.breakeven_year} ans` 
                              : 'Non atteint'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-blue-100 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>💡 Conseil :</strong> {formData.results.rent_vs_buy.recommendation}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ============= GRAPHIQUES D'ANALYSE ============= */}
                  <div className="space-y-6 mt-8">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                      Analyses graphiques
                    </h3>
                    
                    {/* Graphique 1: Évolution du Patrimoine */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h4 className="font-semibold text-slate-800 mb-4">Évolution du patrimoine net</h4>
                      <p className="text-sm text-slate-500 mb-4">Progression de votre richesse sur {formData.holdingDuration || 5} ans</p>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={(() => {
                          const years = parseInt(formData.holdingDuration) || 5;
                          const propertyPrice = parseFloat(formData.propertyPrice) || 150000;
                          const appreciation = parseFloat(formData.expectedAppreciation) || 2;
                          const loanAmount = formData.results?.loan_amount || propertyPrice;
                          const monthlyPayment = formData.results?.monthly_payment || 700;
                          const traditionalCashflow = formData.results?.traditional_cashflow || 0;
                          const airbnbCashflow = formData.results?.airbnb_cashflow || 0;
                          
                          return Array.from({ length: years + 1 }, (_, i) => {
                            const propertyValue = propertyPrice * Math.pow(1 + appreciation / 100, i);
                            const principalPaid = monthlyPayment * 12 * i * 0.6; // ~60% goes to principal over time
                            const remainingLoan = Math.max(0, loanAmount - principalPaid);
                            const equity = propertyValue - remainingLoan;
                            const tradCashflowCumul = traditionalCashflow * 12 * i;
                            const airbnbCashflowCumul = airbnbCashflow * 12 * i;
                            
                            return {
                              year: `Année ${i}`,
                              'Valeur bien': Math.round(propertyValue),
                              'Capital restant': Math.round(remainingLoan),
                              'Patrimoine net': Math.round(equity),
                              'Gain Traditionnel': Math.round(equity + tradCashflowCumul),
                              'Gain Airbnb': Math.round(equity + airbnbCashflowCumul)
                            };
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <Tooltip 
                            formatter={(value) => `${value.toLocaleString('fr-FR')} €`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="Valeur bien" stackId="1" stroke="#3b82f6" fill="#93c5fd" fillOpacity={0.3} />
                          <Area type="monotone" dataKey="Patrimoine net" stackId="2" stroke="#10b981" fill="#6ee7b7" fillOpacity={0.5} />
                          <Line type="monotone" dataKey="Gain Traditionnel" stroke="#2563eb" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="Gain Airbnb" stroke="#f43f5e" strokeWidth={2} dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Graphique 2: Comparaison Cash-flow mensuel */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h4 className="font-semibold text-slate-800 mb-4">Comparaison des cash-flows</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={[
                          {
                            name: 'Revenus',
                            Traditionnel: formData.results?.traditional_detail?.monthly_rent || 0,
                            Airbnb: (formData.results?.airbnb_detail?.gross_annual || 0) / 12
                          },
                          {
                            name: 'Charges',
                            Traditionnel: -(formData.results?.traditional_detail?.monthly_charges || 0),
                            Airbnb: -((formData.results?.charges_detail?.total_monthly || 0) + 
                                      ((formData.results?.airbnb_detail?.platform_fees_annual || 0) + 
                                       (formData.results?.airbnb_detail?.cleaning_costs_annual || 0)) / 12)
                          },
                          {
                            name: 'Mensualité',
                            Traditionnel: -(formData.results?.monthly_payment || 0),
                            Airbnb: -(formData.results?.monthly_payment || 0)
                          },
                          {
                            name: 'Cash-flow',
                            Traditionnel: formData.results?.traditional_cashflow || 0,
                            Airbnb: formData.results?.airbnb_cashflow || 0
                          }
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                          <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
                          <Legend />
                          <Bar dataKey="Traditionnel" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Airbnb" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Graphique 3: Répartition des charges */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h4 className="font-semibold text-slate-800 mb-4">Répartition des charges (annuel)</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Taxe foncière', value: formData.results?.charges_detail?.property_tax_annual || 0 },
                                { name: 'Assurance PNO', value: formData.results?.charges_detail?.insurance_pno_annual || 0 },
                                { name: 'Provision travaux', value: formData.results?.charges_detail?.maintenance_annual || 0 },
                                { name: 'Intérêts crédit', value: (formData.results?.monthly_payment || 0) * 12 * 0.4 }
                              ].filter(d => d.value > 0)}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={false}
                            >
                              {[
                                '#3b82f6', '#f59e0b', '#10b981', '#ef4444'
                              ].map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value.toLocaleString('fr-FR')} €`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Graphique 4: Rendement comparé */}
                      <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h4 className="font-semibold text-slate-800 mb-4">Rendement brut comparé</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart
                            layout="vertical"
                            data={[
                              { name: 'Location traditionnelle', rendement: parseFloat(formData.results?.traditional_yield) || 0 },
                              { name: 'Airbnb / LMNP', rendement: parseFloat(formData.results?.airbnb_yield) || 0 },
                              { name: 'Livret A', rendement: 3.0 },
                              { name: 'Assurance vie fonds €', rendement: 2.5 }
                            ]}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                            <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                            <Bar dataKey="rendement" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                              {[
                                { name: 'Location traditionnelle', fill: '#3b82f6' },
                                { name: 'Airbnb / LMNP', fill: '#f43f5e' },
                                { name: 'Livret A', fill: '#94a3b8' },
                                { name: 'Assurance vie fonds €', fill: '#94a3b8' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Graphique 5: Amortissement du prêt */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                      <h4 className="font-semibold text-slate-800 mb-4">Amortissement du prêt immobilier</h4>
                      <p className="text-sm text-slate-500 mb-4">Évolution du capital restant dû sur {formData.loanDuration || 20} ans</p>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={(() => {
                          const years = parseInt(formData.loanDuration) || 20;
                          const loanAmount = formData.results?.loan_amount || parseFloat(formData.propertyPrice) || 150000;
                          const monthlyPayment = formData.results?.monthly_payment || 700;
                          const annualRate = (formData.results?.interest_rate || parseFloat(formData.interestRate) || 3.2) / 100;
                          const monthlyRate = annualRate / 12;
                          
                          let remaining = loanAmount;
                          let totalInterest = 0;
                          let totalPrincipal = 0;
                          
                          return Array.from({ length: years + 1 }, (_, i) => {
                            if (i === 0) {
                              return {
                                year: `Année ${i}`,
                                'Capital restant': Math.round(loanAmount),
                                'Intérêts cumulés': 0,
                                'Principal remboursé': 0
                              };
                            }
                            
                            for (let m = 0; m < 12 && remaining > 0; m++) {
                              const interestPayment = remaining * monthlyRate;
                              const principalPayment = Math.min(monthlyPayment - interestPayment, remaining);
                              totalInterest += interestPayment;
                              totalPrincipal += principalPayment;
                              remaining = Math.max(0, remaining - principalPayment);
                            }
                            
                            return {
                              year: `Année ${i}`,
                              'Capital restant': Math.round(remaining),
                              'Intérêts cumulés': Math.round(totalInterest),
                              'Principal remboursé': Math.round(totalPrincipal)
                            };
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 11 }} interval={Math.floor((parseInt(formData.loanDuration) || 20) / 5)} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <Tooltip 
                            formatter={(value) => `${value.toLocaleString('fr-FR')} €`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="Capital restant" stroke="#ef4444" fill="#fecaca" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="Principal remboursé" stroke="#10b981" fill="#a7f3d0" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="Intérêts cumulés" stroke="#f59e0b" fill="#fde68a" fillOpacity={0.6} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Graphique 6: Simulation effort d'épargne mensuel */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-6">
                      <h4 className="font-semibold text-purple-800 mb-4">Impact de l'effort d'épargne mensuel</h4>
                      <p className="text-sm text-slate-600 mb-4">Que devient votre patrimoine si vous épargnez en plus du cash-flow ?</p>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={(() => {
                          const years = parseInt(formData.holdingDuration) || 5;
                          const airbnbCashflow = formData.results?.airbnb_cashflow || 0;
                          const traditionalCashflow = formData.results?.traditional_cashflow || 0;
                          const extraSavings = [0, 100, 200, 300]; // Extra monthly savings scenarios
                          
                          return Array.from({ length: years + 1 }, (_, i) => {
                            const base = {
                              year: `Année ${i}`,
                            };
                            
                            extraSavings.forEach(extra => {
                              const monthlyTotal = Math.max(0, airbnbCashflow) + extra;
                              const annualReturn = 0.05; // 5% annual return on savings
                              let accumulated = 0;
                              for (let y = 0; y < i; y++) {
                                accumulated = (accumulated + monthlyTotal * 12) * (1 + annualReturn);
                              }
                              base[`+${extra}€/mois`] = Math.round(accumulated);
                            });
                            
                            return base;
                          });
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <Tooltip 
                            formatter={(value) => `${value.toLocaleString('fr-FR')} €`}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="+0€/mois" stroke="#94a3b8" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="+100€/mois" stroke="#3b82f6" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="+200€/mois" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="+300€/mois" stroke="#10b981" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Les résultats seront affichés après le calcul</p>
                  <Button onClick={calculateResults} className="mt-4 bg-purple-600 hover:bg-purple-700 text-white">
                    Lancer le calcul
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 10: Project Context (qualitative for banker) */}
          {currentStep === 10 && (
            <div data-testid="step-10-content">
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Contexte du Projet</h2>
                    <p className="text-sm text-slate-500">Renforcez votre dossier avec les éléments humains et stratégiques</p>
                  </div>
                </div>
              </div>
              <ProjectContextStep
                formData={formData}
                handleInputChange={(e) => updateFormData(e.target.name, e.target.value)}
                errors={{}}
              />
              {contextSummary && (
                <div className="mt-6 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-emerald-600" />
                    <h4 className="font-semibold text-emerald-900">Synthèse IA générée</h4>
                  </div>
                  <p className="text-emerald-900 text-sm italic leading-relaxed">{contextSummary}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 11: PDF Generation */}
          {currentStep === 11 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="step-8-content">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Génération du Dossier PDF</h2>
                  <p className="text-sm text-slate-500">Votre dossier professionnel pour la banque</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Contenu du dossier</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Synthèse executive de l'investissement</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Analyse de marché détaillée (sources Airbnb & INSEE)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Simulation financière complète</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Projection de cash-flow sur {formData.loanDuration} ans</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Stratégie de sortie et plus-value estimée</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-slate-700">Graphiques et visualisations</span>
                    </li>
                  </ul>
                </div>

                {/* VIP: Free PDF download */}
                {currentUser?.is_vip ? (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border-2 border-amber-300 text-center">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="px-3 py-1 bg-amber-500 text-white text-sm font-bold rounded-full">VIP</span>
                      <span className="text-2xl font-bold text-green-600">GRATUIT</span>
                    </div>
                    <p className="text-sm text-slate-600 mb-6">
                      En tant que membre VIP, vous bénéficiez du téléchargement gratuit et illimité.
                    </p>
                    <Button 
                      onClick={generatePdfVip}
                      disabled={pdfGenerating || !simulationId}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6 text-lg"
                      data-testid="btn-generate-pdf-vip"
                    >
                      {pdfGenerating ? (
                        <>
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2 h-5 w-5" />
                          Télécharger le PDF gratuitement
                        </>
                      )}
                    </Button>
                    
                    {/* Word download button */}
                    <Button
                      onClick={handleDownloadWord}
                      disabled={wordGenerating || !simulationId}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 mt-3"
                      data-testid="btn-generate-word-vip"
                    >
                      {wordGenerating ? (
                        <>
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Génération Word...
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2 h-4 w-4" />
                          Télécharger en Word (.docx)
                        </>
                      )}
                    </Button>
                    
                    {!simulationId && formData.results && (
                      <Button
                        onClick={async () => {
                          try {
                            setLoading(true);
                            await saveSimulation(formData.results);
                            toast.success('Simulation sauvegardée !');
                          } catch (err) {
                            console.error('Retry save error:', err);
                            toast.error('Erreur lors de la sauvegarde. Réessayez.');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 mt-3"
                        data-testid="btn-retry-save"
                      >
                        {loading ? (
                          <>
                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Sauvegarde en cours...
                          </>
                        ) : (
                          <>
                            <AlertCircle className="mr-2 h-4 w-4" />
                            Réessayer la sauvegarde
                          </>
                        )}
                      </Button>
                    )}
                    
                    {!simulationId && !formData.results && (
                      <p className="text-xs text-red-500 mt-2">
                        Veuillez d'abord calculer les résultats (étape précédente)
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                    <div className="text-center mb-6">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-4xl font-bold text-slate-800">
                          {premiumDocumentUsed ? '5,99€' : '4,99€'}
                        </span>
                        <span className="text-slate-500">TTC</span>
                      </div>
                      {premiumDocumentUsed && (
                        <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-3">
                          <p className="text-sm text-amber-800 font-medium">
                            4,99€ + 1,00€ (analyse document premium)
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Document {premiumDocumentType === 'promesse_vente' ? 'promesse de vente' : 'fiche agence'} joint automatiquement au dossier
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-slate-600 mt-2">
                        Paiement sécurisé • Téléchargement immédiat
                      </p>
                    </div>
                    
                    {/* Payment Method Selection */}
                    <div className="mb-6">
                      <p className="text-sm font-medium text-slate-700 mb-3">Choisissez votre mode de paiement :</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setSelectedPaymentMethod('stripe')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            selectedPaymentMethod === 'stripe' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          data-testid="select-stripe"
                        >
                          <CreditCard className={`h-6 w-6 ${selectedPaymentMethod === 'stripe' ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${selectedPaymentMethod === 'stripe' ? 'text-blue-600' : 'text-slate-600'}`}>
                            Carte bancaire
                          </span>
                          <span className="text-xs text-slate-400">via Stripe</span>
                        </button>
                        
                        <button
                          onClick={() => setSelectedPaymentMethod('paypal')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            selectedPaymentMethod === 'paypal' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          data-testid="select-paypal"
                        >
                          <svg className={`h-6 w-6 ${selectedPaymentMethod === 'paypal' ? 'text-blue-600' : 'text-slate-400'}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19a.778.778 0 0 0-.77.657l-1.4 8.349z"/>
                          </svg>
                          <span className={`text-sm font-medium ${selectedPaymentMethod === 'paypal' ? 'text-blue-600' : 'text-slate-600'}`}>
                            PayPal
                          </span>
                          <span className="text-xs text-slate-400">compte PayPal</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Pay Button */}
                    <Button 
                      onClick={handlePayment}
                      disabled={paymentProcessing || !simulationId}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                      data-testid="btn-pay-and-download"
                    >
                      {paymentProcessing ? (
                        <>
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          {selectedPaymentMethod === 'stripe' ? (
                            <CreditCard className="mr-2 h-5 w-5" />
                          ) : (
                            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19a.778.778 0 0 0-.77.657l-1.4 8.349z"/>
                            </svg>
                          )}
                          Payer et télécharger le PDF
                        </>
                      )}
                    </Button>
                    
                    {!simulationId && formData.results && (
                      <Button
                        onClick={async () => {
                          try {
                            setLoading(true);
                            await saveSimulation(formData.results);
                            toast.success('Simulation sauvegardée !');
                          } catch (err) {
                            console.error('Retry save error:', err);
                            toast.error('Erreur lors de la sauvegarde. Réessayez.');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 mt-3"
                        data-testid="btn-retry-save-payment"
                      >
                        {loading ? 'Sauvegarde en cours...' : 'Réessayer la sauvegarde'}
                      </Button>
                    )}
                    
                    {!simulationId && !formData.results && (
                      <p className="text-xs text-red-500 mt-2 text-center">
                        Veuillez d'abord calculer les résultats (étape précédente)
                      </p>
                    )}
                    
                    <p className="text-xs text-slate-400 mt-4 text-center">
                      Paiement sécurisé par {selectedPaymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              onClick={goBack}
              variant="outline"
              disabled={currentStep === 1}
              className="px-6"
              data-testid="btn-previous"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Précédent
            </Button>
            
            {/* Validation message */}
            {!canGoNext() && getValidationMessage() && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{getValidationMessage()}</span>
              </div>
            )}
            
            {currentStep < STEPS.length && (
              <Button
                onClick={goNext}
                disabled={!canGoNext() || contextSaving}
                className="px-6 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-next"
              >
                {contextSaving && currentStep === 10
                  ? 'Synthèse IA en cours…'
                  : currentStep === 8
                    ? 'Calculer'
                    : currentStep === 10
                      ? 'Générer la synthèse & continuer'
                      : 'Suivant'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Auto-fill Modal */}
      {showAutoFill && (
        <PropertyAutoFill 
          onDataExtracted={handleAutoFillData}
          onClose={() => setShowAutoFill(false)}
        />
      )}
    </div>
  );
}
